import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Send, Search, ChevronLeft, Sparkles, Star, Flag, MoreVertical, Ban, ShieldOff, Check, X, User } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { UserAvatar, RankBadge } from '../components/RankBadge';
import { Input } from '../components/ui/input';
import ChatComposer from '../components/ChatComposer';
import GiftFly from '../components/GiftFly';
import DateInviteCard from '../components/DateInviteCard';
import ReportUserDialog from '../components/ReportUserDialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import VipBadge from '../components/VipBadge';
import { toast } from 'sonner';
import ws, { useChannel } from '../lib/ws';

const DATE_INVITE_PRESETS = [
  { kind: 'Coffee', emoji: '☕' },
  { kind: 'Walk', emoji: '🚶' },
  { kind: 'Movie', emoji: '🎬' },
  { kind: 'Dinner', emoji: '🍽️' },
];

// Compact human-readable "last seen" — matches the tone used in Meta / iOS
// messages. Falls back to "recently" if the timestamp is missing or unparsable.
function relativeTime(iso) {
  if (!iso) return 'recently';
  const then = new Date(iso).getTime();
  if (!then) return 'recently';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const wks = Math.floor(days / 7);
  if (wks < 5) return `${wks}w ago`;
  const mos = Math.floor(days / 30);
  return `${mos}mo ago`;
}

export default function Messages() {
  const { user, refresh, openBuyStars, creditStars, bumpStats, setChatFocused } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [showList, setShowList] = useState(true);
  const [online, setOnline] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [fly, setFly] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [thread, setThread] = useState(null);   // {status, is_requester, blocked_by_me, blocked_by_other}
  const scrollRef = useRef(null);
  // Typing indicator: `typingByUser` maps sender_id → expiry timestamp (ms).
  // An entry is "live" while now < expiry (5s TTL per ping). Consumers filter
  // both self-pings (never render own dots) and pings for non-active threads
  // where appropriate.
  const [typingByUser, setTypingByUser] = useState({});
  const lastTypingSentRef = useRef(0);

  // Hide the mobile bottom nav while a DM thread is open so the chat
  // composer is not covered. Applies to BOTH mobile and desktop — the
  // fixed nav is 56–72px tall on desktop too and would clip the input.
  // Any active thread means the user is chatting, so focus the surface.
  useEffect(() => {
    setChatFocused?.(!!active);
    return () => setChatFocused?.(false);
  }, [active, setChatFocused]);

  useEffect(() => {
    const load = async () => {
      try {
        const [tRes, oRes] = await Promise.all([api.get('/dms'), api.get('/users/online')]);
        setThreads(tRes.data); setOnline(oRes.data);

        // Deep-link: `/app/messages?to=<user_id>` — open (or start) the thread
        // with that user immediately. This is the entry point from the
        // "Message" button on public profiles and other CTAs.
        const toId = searchParams.get('to');
        if (toId) {
          // Prefer an existing thread — carries unread/blocked state.
          const existing = tRes.data.find(t => t.user?.user_id === toId);
          if (existing?.user) {
            setActive(existing.user);
          } else {
            // No thread yet → fetch the target user's public profile so we
            // can render the header + composer for a brand-new conversation.
            try {
              const { data: target } = await api.get(`/users/${toId}`);
              setActive({
                user_id: target.user_id,
                displayName: target.displayName,
                avatar: target.avatar,
                rank: target.rank,
                is_vip: target.is_vip,
                country: target.country,
                is_online: target.is_online,
                last_seen: target.last_seen,
              });
            } catch {
              // Fall through to the default-thread selection below
            }
          }
          // Force the chat panel on mobile so the composer is visible right
          // away — without this, `showList` stayed true and the message
          // input remained hidden under the bottom nav on entry.
          setShowList(false);
          // Clean the URL so refresh doesn't keep re-selecting the same user
          setSearchParams({}, { replace: true });
          return;
        }

        if (!active && tRes.data[0]) setActive(tRes.data[0].user);
        else if (!active && oRes.data[0]) setActive(oRes.data[0]);
      } catch { /* fail – will retry on next user action */ }
    };
    load();
    api.get('/gifts').then(r => setGifts(r.data || [])).catch(() => {});
    // Refresh threads + online list every 60s so the online-dot / "Active
    // now" indicator ages naturally as people close their tabs. WS still
    // pushes new messages instantly; this is just presence bookkeeping.
    const iv = setInterval(() => {
      api.get('/dms').then(r => {
        setThreads(r.data);
        // Also refresh the CURRENTLY-OPEN header's presence so "Active now"
        // flips to "Last seen X ago" without needing the user to re-open.
        setActive(prev => {
          if (!prev) return prev;
          const match = r.data.find(t => t.user?.user_id === prev.user_id);
          return match ? { ...prev, is_online: match.user.is_online, last_seen: match.user.last_seen } : prev;
        });
      }).catch(() => {});
      api.get('/users/online').then(r => setOnline(r.data)).catch(() => {});
    }, 60_000);
    return () => clearInterval(iv);
    // No HTTP polling for messages — those flow over WebSocket.
  }, [searchParams]);

  // Live: when a new DM lands on any of my threads, refresh thread list snapshot.
  // Also surface typing indicators from ANY thread on my user channel so the
  // Mail list can show a "typing…" hint for threads that aren't focused.
  useChannel(user?.user_id ? `user:${user.user_id}` : null, (evt) => {
    if (evt?.type === 'dm.typing' && evt.from && evt.from !== user.user_id) {
      const expiry = Date.now() + 5000;
      setTypingByUser(prev => ({ ...prev, [evt.from]: expiry }));
      return;
    }
    api.get('/dms').then(r => setThreads(r.data)).catch(() => {});
  }, [user?.user_id]);

  // Live: append/update messages for the currently open conversation.
  const threadKey = user && active ? ws.dmThreadKey(user.user_id, active.user_id) : null;
  useChannel(threadKey ? `dm:${threadKey}` : null, (evt) => {
    if (evt?.type === 'dm.typing') {
      if (evt.from && evt.from !== user?.user_id) {
        const expiry = Date.now() + 5000;
        setTypingByUser(prev => ({ ...prev, [evt.from]: expiry }));
      }
      return;
    }
    if (!evt.message) return;
    if (evt.type === 'dm.message') {
      setMessages(prev => prev.some(m => m.id === evt.message.id) ? prev : [...prev, evt.message]);
      // Clear any stale typing indicator now that we have their message
      setTypingByUser(prev => {
        if (!prev[evt.message.sender_id]) return prev;
        const { [evt.message.sender_id]: _drop, ...rest } = prev;
        return rest;
      });
    } else if (evt.type === 'dm.message.update') {
      setMessages(prev => prev.map(m => m.id === evt.message.id ? { ...m, ...evt.message } : m));
    }
    // Also refresh thread previews (last message + unread bumps)
    api.get('/dms').then(r => setThreads(r.data)).catch(() => {});
  }, [threadKey, user?.user_id]);

  // Periodically prune expired typing entries so stale dots vanish even
  // when the other user closes their tab mid-typing (no more pings ever).
  useEffect(() => {
    const iv = setInterval(() => {
      const now = Date.now();
      setTypingByUser(prev => {
        let changed = false;
        const next = {};
        for (const [uid, exp] of Object.entries(prev)) {
          if (exp > now) next[uid] = exp;
          else changed = true;
        }
        return changed ? next : prev;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!active) return;
    api.get(`/dms/${active.user_id}/messages`).then(r => setMessages(r.data)).catch(() => {});
    api.get(`/dms/${active.user_id}/thread`).then(r => setThread(r.data)).catch(() => setThread(null));
  }, [active]);

  const loadThread = () => {
    if (!active) return;
    api.get(`/dms/${active.user_id}/thread`).then(r => setThread(r.data)).catch(() => {});
  };

  const acceptRequest = async () => {
    if (!active) return;
    try {
      await api.post(`/dms/${active.user_id}/accept`);
      toast.success(`Message request from ${active.displayName} accepted`);
      loadThread();
      api.get('/dms').then(r => setThreads(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not accept request');
    }
  };

  const declineRequest = async () => {
    if (!active) return;
    if (!window.confirm(`Decline message request from ${active.displayName}? They will be blocked from messaging you again.`)) return;
    try {
      await api.post(`/dms/${active.user_id}/decline`);
      toast.success('Request declined');
      setActive(null); setShowList(true); setMessages([]); setThread(null);
      api.get('/dms').then(r => setThreads(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not decline');
    }
  };

  const blockUser = async () => {
    if (!active) return;
    if (!window.confirm(`Block ${active.displayName}? You won't be able to message each other. You can unblock them anytime from settings.`)) return;
    try {
      await api.post(`/dms/${active.user_id}/block`);
      toast.success(`${active.displayName} blocked`);
      setActive(null); setShowList(true); setMessages([]); setThread(null);
      api.get('/dms').then(r => setThreads(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not block user');
    }
  };

  const unblockUser = async () => {
    if (!active) return;
    try {
      await api.post(`/dms/${active.user_id}/unblock`);
      toast.success(`${active.displayName} unblocked`);
      loadThread();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not unblock user');
    }
  };

  // Scroll pinned to bottom. Instant on the first mount / thread swap so the
  // user lands at the newest message immediately; smooth for incoming lines.
  // Re-pins after chat images finish loading (layout shifts otherwise).
  const didInitialScrollRef = useRef(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const behavior = didInitialScrollRef.current ? 'smooth' : 'auto';
    el.scrollTo({ top: el.scrollHeight, behavior });
    didInitialScrollRef.current = true;
    const imgs = el.querySelectorAll('img');
    let pending = 0;
    const onLoad = () => {
      pending -= 1;
      if (pending <= 0) el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
    };
    imgs.forEach(img => {
      if (!img.complete) {
        pending += 1;
        img.addEventListener('load', onLoad, { once: true });
        img.addEventListener('error', onLoad, { once: true });
      }
    });
  }, [messages]);

  // Reset "first-mount" pinning when the active thread changes so switching
  // conversations always lands the user at the latest message instantly.
  useEffect(() => { didInitialScrollRef.current = false; }, [active?.user_id]);

  const send = async (overrideText, extras = {}) => {
    const body = (overrideText ?? text).trim();
    if (!body || !active) return;
    try {
      const payload = { text: body };
      if (extras.image_url) payload.image_url = extras.image_url;
      const { data } = await api.post(`/dms/${active.user_id}/messages`, payload);
      setMessages(prev => prev.some(m => m.id === data.id) ? prev : [...prev, data]);
      // +5 XP per DM (mirror server)
      bumpStats({ xp: 5 });
      if (overrideText === undefined) setText('');
      // Refresh thread meta — auto-accept flips pending → accepted
      loadThread();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send message');
      if (e?.response?.status === 403) loadThread();
      throw e;
    }
  };

  const sendGift = async (gift) => {
    if (!active) return;
    if ((user?.stars ?? 0) < gift.price) {
      const need = gift.price - (user?.stars ?? 0);
      toast.error(`Need ${need.toLocaleString()} more ⭐ for ${gift.name}. Top up to send it!`);
      openBuyStars();
      throw new Error('insufficient_stars'); // close the picker
    }
    try {
      await api.post('/gifts/send', { gift_id: gift.id, recipient_id: active.user_id });
      creditStars(-gift.price);
      setFly({ emoji: gift.emoji, name: `${gift.name} → ${active.displayName}` });
      toast.success(`Sent ${gift.name} ${gift.emoji}`);
      // WS will deliver the inline gift message — skip the refetch
      refresh?.();
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (e.response?.status === 400 && /not enough stars/i.test(detail)) {
        toast.error('Not enough stars — open Buy Stars to top up');
        openBuyStars();
        throw e;
      }
      toast.error(detail || 'Could not send gift');
      throw e;
    }
  };

  const sendDateInvite = async (payload) => {
    if (!active) return;
    try {
      const { data } = await api.post(`/dms/${active.user_id}/date-invite`, payload);
      setMessages(prev => prev.some(m => m.id === data.id) ? prev : [...prev, data]);
      toast.success(`${payload.emoji} ${payload.kind} invite sent`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send invite');
      throw e;
    }
  };

  const updateInviteMsg = (updated) => {
    setMessages(prev => prev.map(m => m.id === updated.id ? { ...m, ...updated } : m));
  };

  // Merge existing DM threads with all currently-online users so the count
  // matches the home page "online now" card. Threads come first (sorted by
  // most recent activity from the backend), online-only users follow.
  const threadIds = new Set(threads.map(t => t.user.user_id));
  const onlineOnly = online
    .filter(u => u.user_id !== user?.user_id && !threadIds.has(u.user_id))
    .map(u => ({ user: u, last: 'Online — say hi', time: '', unread: 0, isOnlineStub: true }));
  const fullList = [...threads, ...onlineOnly];
  const q = query.trim().toLowerCase();
  const list = q
    ? fullList.filter(t =>
        t.user.displayName?.toLowerCase().includes(q) ||
        t.user.username?.toLowerCase().includes(q) ||
        (typeof t.last === 'string' && t.last.toLowerCase().includes(q))
      )
    : fullList;

  return (
    <div
      className="-mx-4 lg:-mx-8 -mt-5 -mb-5 lg:-mb-8 grid lg:grid-cols-[320px_1fr] h-[calc(100dvh-96px-1.25rem-env(safe-area-inset-bottom))] sm:h-[calc(100dvh-64px-1.25rem-env(safe-area-inset-bottom))]"
    >
      <aside className={`${showList ? 'block' : 'hidden'} lg:block border-r border-white/5 bg-[#10101a] overflow-y-auto scroll-hide`}>
        <div className="p-4 sticky top-0 bg-[#10101a]/95 backdrop-blur z-10">
          <h2 className="font-display font-bold text-xl mb-3">Mail</h2>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="messages-search-input"
              className="pl-8 pr-8 bg-white/5 border-white/10 text-white placeholder:text-white/40 h-9"
              placeholder="Search by name…"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                data-testid="messages-search-clear"
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white text-xs flex items-center justify-center"
              >
                ×
              </button>
            )}
          </div>
        </div>
        <div className="px-2 pb-4">
          {list.length === 0 && query ? (
            <div className="px-3 py-8 text-center text-xs text-white/40" data-testid="messages-search-empty">
              No travelers match <span className="text-white/70">&ldquo;{query}&rdquo;</span>
            </div>
          ) : list.map(t => {
            const isOn = !!t.user.is_online;
            const isTyping = typingByUser[t.user.user_id] && Date.now() < typingByUser[t.user.user_id];
            return (
            <button key={t.user.user_id} onClick={() => { setActive(t.user); setShowList(false); api.post(`/users/${t.user.user_id}/view`).catch(() => {}); }} data-testid={`mail-thread-${t.user.user_id}`} className={`w-full flex items-center gap-3 p-3 rounded-xl ${active?.user_id === t.user.user_id ? 'bg-white/10' : 'hover:bg-white/5'}`}>
              <div className="relative">
                <UserAvatar user={t.user} size={42} />
                {isOn && (
                  <span
                    className="absolute -bottom-0.5 -right-0.5 block w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#161622]"
                    title="Online now"
                    data-testid={`mail-online-dot-${t.user.user_id}`}
                  />
                )}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm truncate flex items-center gap-1.5">
                    {t.user.displayName}
                    <VipBadge active={t.user.is_vip} size="sm" />
                    {t.is_request && (
                      <span className="text-[9px] uppercase tracking-wider bg-amber-500/20 text-amber-200 border border-amber-400/40 px-1.5 rounded-full flex items-center gap-0.5" data-testid="mail-request-badge">
                        <Sparkles size={7} /> Request
                      </span>
                    )}
                  </span>
                  {t.time && <span className="text-[11px] text-white/40">{new Date(t.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
                </div>
                <div className="flex items-center justify-between mt-0.5">
                  {isTyping ? (
                    <span className="text-xs text-cyan-300 flex items-center gap-1" data-testid={`mail-typing-${t.user.user_id}`}>
                      <span className="flex gap-0.5">
                        <span className="w-1 h-1 rounded-full bg-cyan-300 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1 h-1 rounded-full bg-cyan-300 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1 h-1 rounded-full bg-cyan-300 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </span>
                      typing…
                    </span>
                  ) : (
                    <span className={`text-xs truncate ${isOn ? 'text-emerald-300/80' : 'text-white/50'}`}>
                      {isOn && <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1 align-middle" />}
                      {t.last}
                    </span>
                  )}
                  {t.unread > 0 && <span className="ml-2 bg-fuchsia-500 text-white text-[10px] font-semibold px-1.5 rounded-full">{t.unread}</span>}
                </div>
              </div>
            </button>
            );
          })}
        </div>
      </aside>

      <div className={`${showList ? 'hidden' : 'flex'} lg:flex flex-col min-h-0`}>
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-white/40">Select a conversation</div>
        ) : (
          <>
            <div className="flex items-center gap-3 px-4 lg:px-6 py-3 border-b border-white/5 bg-[#10101a]">
              <button onClick={() => setShowList(true)} className="lg:hidden p-1 text-white/70"><ChevronLeft size={20} /></button>
              <Link to={`/app/users/${active.user_id}`} title={`View ${active.displayName}'s profile`} data-testid="dm-header-avatar">
                <UserAvatar user={active} size={40} />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/app/users/${active.user_id}`} className="flex items-center gap-2 hover:opacity-90" data-testid="dm-header-name">
                  <span className="font-semibold hover:underline">{active.displayName}</span>
                  <RankBadge rank={active.rank} />
                  <VipBadge active={active.is_vip} size="sm" data-testid="mail-header-vip-badge" />
                </Link>
                <div className={`text-[11px] ${active.is_online ? 'text-emerald-400' : 'text-white/40'}`} data-testid="dm-header-presence">
                  {typingByUser[active.user_id] && Date.now() < typingByUser[active.user_id] ? (
                    <span className="flex items-center gap-1 text-cyan-300" data-testid="dm-typing-header">
                      <span className="flex gap-0.5">
                        <span className="w-1 h-1 rounded-full bg-cyan-300 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1 h-1 rounded-full bg-cyan-300 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1 h-1 rounded-full bg-cyan-300 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </span>
                      typing…
                    </span>
                  ) : active.is_online ? (
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      Active now
                    </span>
                  ) : (
                    active.last_seen
                      ? `Last seen ${relativeTime(active.last_seen)}`
                      : 'Offline'
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setReportOpen(true)}
                data-testid="dm-report-btn"
                className="p-2 rounded-full text-white/40 hover:text-rose-300 hover:bg-rose-500/10 transition"
                title={`Report ${active.displayName}`}
                aria-label="Report this user"
              >
                <Flag size={16} />
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="dm-more-btn"
                    className="p-2 -mr-1 rounded-full text-white/40 hover:text-white hover:bg-white/10 transition"
                    aria-label="More actions"
                  >
                    <MoreVertical size={16} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-[#161622] border-white/10 text-white min-w-[180px]">
                  <DropdownMenuItem
                    onClick={() => nav(`/app/users/${active.user_id}`)}
                    data-testid="dm-view-profile-item"
                    className="hover:bg-white/10 cursor-pointer text-cyan-300"
                  >
                    <User size={14} className="mr-2" /> View profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/5" />
                  {thread?.blocked_by_me ? (
                    <DropdownMenuItem
                      onClick={unblockUser}
                      data-testid="dm-unblock-item"
                      className="hover:bg-white/10 cursor-pointer text-emerald-300"
                    >
                      <ShieldOff size={14} className="mr-2" /> Unblock user
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      onClick={blockUser}
                      data-testid="dm-block-item"
                      className="hover:bg-rose-500/10 cursor-pointer text-rose-300 focus:text-rose-300"
                    >
                      <Ban size={14} className="mr-2" /> Block user
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/5" />
                  <DropdownMenuItem
                    onClick={() => setReportOpen(true)}
                    className="hover:bg-white/10 cursor-pointer text-white/80"
                  >
                    <Flag size={14} className="mr-2" /> Report user
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Consent + block banners */}
            {thread?.blocked_by_me && (
              <div className="px-4 lg:px-6 py-3 bg-rose-500/10 border-b border-rose-500/20 text-sm text-rose-200 flex items-center gap-3" data-testid="dm-blocked-by-me-banner">
                <Ban size={16} className="text-rose-300 shrink-0" />
                <span className="flex-1">You blocked {active.displayName}. They can&apos;t see or message you.</span>
                <button onClick={unblockUser} data-testid="dm-unblock-banner-btn" className="text-xs font-semibold text-emerald-300 hover:text-emerald-200 underline">Unblock</button>
              </div>
            )}
            {thread?.blocked_by_other && !thread?.blocked_by_me && (
              <div className="px-4 lg:px-6 py-3 bg-white/5 border-b border-white/10 text-sm text-white/60 flex items-center gap-2" data-testid="dm-blocked-by-other-banner">
                <Ban size={16} className="text-white/50 shrink-0" />
                <span>You can no longer message this user.</span>
              </div>
            )}
            {thread?.status === 'pending' && !thread?.is_requester && !thread?.blocked_by_me && !thread?.blocked_by_other && (
              <div className="px-4 lg:px-6 py-3 bg-amber-500/10 border-b border-amber-500/25 flex items-center gap-3" data-testid="dm-request-banner">
                <Sparkles size={16} className="text-amber-300 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-amber-200">Message request</div>
                  <div className="text-[11px] text-amber-100/70">{active.displayName} wants to send you a message. Reply to accept, or decline to block them.</div>
                </div>
                <button
                  onClick={acceptRequest}
                  data-testid="dm-accept-btn"
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold"
                >
                  <Check size={12} /> Accept
                </button>
                <button
                  onClick={declineRequest}
                  data-testid="dm-decline-btn"
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/5 hover:bg-rose-500/20 border border-white/10 hover:border-rose-500/40 text-white/80 hover:text-rose-200 text-xs font-semibold"
                >
                  <X size={12} /> Decline
                </button>
              </div>
            )}
            {thread?.status === 'pending' && thread?.is_requester && (
              <div className="px-4 lg:px-6 py-2 bg-white/5 border-b border-white/10 text-[11px] text-white/50 text-center" data-testid="dm-waiting-banner">
                Waiting for {active.displayName} to accept your message request…
              </div>
            )}
            <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-hide px-4 lg:px-8 py-6 space-y-3">
              {messages.length === 0 && <div className="text-center text-white/40 py-10 text-sm">Say hi to {active.displayName} \u2728</div>}
              {messages.map(m => {
                const mine = m.sender_id === user?.user_id;
                const legacyDataImg = typeof m.text === 'string' && m.text.startsWith('data:image/');
                const imageSrc = m.image_url
                  ? (m.image_url.startsWith('http') ? m.image_url : `${process.env.REACT_APP_BACKEND_URL || ''}${m.image_url}`)
                  : (legacyDataImg ? m.text : null);
                const isImage = !!imageSrc;
                if (m.isDateInvite && m.dateInvite) {
                  return (
                    <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <DateInviteCard msg={m} mine={mine} onUpdate={updateInviteMsg} />
                    </div>
                  );
                }
                if (m.isGift && m.gift) {
                  return (
                    <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className="max-w-[78%] px-4 py-3 rounded-2xl text-sm flex items-center gap-3"
                        style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.18), rgba(244,114,182,0.12))', border: '1px solid rgba(251,191,36,0.35)' }}
                        data-testid="mail-gift-msg"
                      >
                        <div className="text-4xl">{m.gift.emoji}</div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1 text-amber-200 font-semibold">
                            <Sparkles size={12} /> {m.gift.name}
                          </div>
                          <div className="text-[11px] text-amber-100/70 flex items-center gap-1 mt-0.5">
                            <Star size={9} fill="currentColor" />{m.gift.price?.toLocaleString?.() || m.gift.price}
                            <span className="text-white/40 ml-2">{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] ${isImage ? 'p-1.5' : 'px-4 py-2'} rounded-2xl text-sm ${mine ? 'bg-gradient-to-br from-fuchsia-500 to-cyan-500 text-white' : 'bg-white/8 text-white/90'}`}>
                      {isImage
                        ? <img src={imageSrc} alt="" loading="lazy" decoding="async" className="rounded-xl max-w-[260px] max-h-[300px] object-cover block" />
                        : m.text}
                      <div className={`text-[10px] mt-1 ${isImage ? 'px-2 pb-1' : ''} ${mine ? 'text-white/70' : 'text-white/40'}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <ChatComposer
              testIdPrefix="mail"
              placeholder={
                thread?.blocked_by_me ? 'Unblock to send messages…'
                : thread?.blocked_by_other ? 'You can no longer message this user'
                : 'Type a message...'
              }
              disabled={!!(thread?.blocked_by_me || thread?.blocked_by_other)}
              onSend={(body, extras) => send(body, extras)}
              onTyping={() => {
                // Throttle to at most once every 3s per active thread.
                if (!active?.user_id) return;
                const now = Date.now();
                if (now - lastTypingSentRef.current < 3000) return;
                lastTypingSentRef.current = now;
                api.post(`/dms/${active.user_id}/typing`).catch(() => {});
              }}
              gifts={gifts}
              onSendGift={sendGift}
              dateInvites={DATE_INVITE_PRESETS}
              onSendDateInvite={sendDateInvite}
            />
          </>
        )}
      </div>
      {fly && <GiftFly emoji={fly.emoji} name={fly.name} onDone={() => setFly(null)} />}
      <ReportUserDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        target={active ? { user_id: active.user_id, displayName: active.displayName } : null}
      />
    </div>
  );
}
